import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import settings from '/@/core/settings';
import { useGetCornDiseaseControlApplications } from '/@/hooks/pathologies';
import {
    getClimatePatternChoices,
    useAllSoyPlantingRecommendation,
    useCornDensityCurveForProducts,
    useEnvironments,
    useFeaturesForProducts,
    useFertilizationCoefficientsForProducts,
    useMetricsForProducts,
    usePestControlPlans,
    usePests,
    usePhenologyForProducts,
    useProducts,
    useSoilMoistures,
    useSoilTypesAndIrrigationChoices,
    useYieldByEnvironmentForProducts,
} from '/@/hooks/recommendation';
import { getCatalogBrands } from '/@/store/catalog/selectors';
import { getCropConfig, getValidPlantingDates } from '/@/store/private-data/recommendations/selectors';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getSessionBrands } from '/@/store/session/selectors';
import { groupBy, localeSort } from '/@/utils';

export const useRecommendationEditData = ({ field, crop, handleChange }) => {
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const plantingDate = query.get('planting_date');
    const climatePattern = query.get('climate_pattern');
    const soilMoisture = query.get('soil_moisture');
    const productProfile = query.get('product_profile');

    const recommendation = useSelector(getActiveRecommendation);
    const sessionBrands = useSelector(getSessionBrands);

    const cropConfig = getCropConfig(recommendation, crop);

    // Set URL parameters if they exist
    useEffect(() => {
        if (plantingDate) {
            handleChange('plantingDate')(plantingDate);
        }
        if (climatePattern) {
            handleChange('climatePattern')(climatePattern);
        }
    }, [plantingDate, climatePattern]);

    // Data
    const hideSoilMoisture = field?.country.code === 'MX' && crop === 'corn';

    const enableSoilMoisturesQuery = crop === 'corn' && !hideSoilMoisture;
    const soilMoisturesQuery = useSoilMoistures(
        {
            lat: field?.lat,
            lng: field?.lng,
            countryCode: field?.country?.code,
            climatePattern: cropConfig?.climatePattern,
            plantingDate: cropConfig?.plantingDate,
        },
        { enabled: enableSoilMoisturesQuery }
    );

    useEffect(() => {
        const soilMoistures = soilMoisturesQuery.data;
        if (crop === 'corn' && soilMoistures) {
            if (soilMoistures.length === 0) {
                handleChange('soilMoisture')(null);
                handleChange('environment')(9000);
            } else {
                if (soilMoisture) {
                    handleChange('soilMoisture')(soilMoisture);
                } else if (!soilMoistures.some((el) => el.value === cropConfig?.soilMoisture)) {
                    handleChange('soilMoisture')(
                        soilMoistures.length
                            ? soilMoistures[Math.min(Math.floor(soilMoistures.length / 2), soilMoistures.length - 1)]
                                  .value
                            : null
                    );
                }
            }
        }
    }, [crop, soilMoisture, soilMoisturesQuery?.data]);

    const enableSoilTypesQuery =
        crop === 'corn' && (!enableSoilMoisturesQuery || ![undefined, null].includes(cropConfig?.soilMoisture));
    const soilTypesQuery = useSoilTypesAndIrrigationChoices(
        {
            lat: field?.lat,
            lng: field?.lng,
            countryCode: field?.country?.code,
            climatePattern: cropConfig?.climatePattern,
            soilMoisture: cropConfig?.soilMoisture,
            plantingDate: cropConfig?.plantingDate,
        },
        {
            enabled: enableSoilTypesQuery,
        }
    );

    useEffect(() => {
        const soilTypes = soilTypesQuery.data?.soilTypesChoices;
        const irrigationChoices = soilTypesQuery.data?.irrigationChoices;

        if (crop === 'corn') {
            if (soilTypes?.length === 0 && irrigationChoices?.length === 0) {
                handleChange('environment')(9000);
            }
            // Select default soilType
            // if the selected soilType is not more an option or if is null
            if (
                soilTypes &&
                (!cropConfig?.soilType ||
                    !soilTypes.some(
                        (soilType) => soilType === cropConfig?.soilType || soilType?.value === cropConfig?.soilType
                    ))
            ) {
                const hasNull = soilTypes.some((soilType) => soilType === null);
                if (hasNull) {
                    if (hasNull && cropConfig?.soilType !== null) {
                        handleChange('soilType')(null);
                    }
                } else {
                    handleChange('soilType')(soilTypes.length > 0 ? soilTypes[0].value : null);
                }
            }
        }
    }, [crop, cropConfig?.soilType, soilTypesQuery.data]);

    useEffect(() => {
        const irrigationChoices = soilTypesQuery.data?.irrigationChoices;

        if (crop === 'corn') {
            // Select default irrigation
            // if the selected irrigation is not more an option or if is null
            if (
                irrigationChoices &&
                (!cropConfig?.irrigation ||
                    !irrigationChoices.some((irrigation) => irrigation?.value === cropConfig?.irrigation))
            ) {
                const hasNull = irrigationChoices.some((irrigation) => irrigation === null);
                if (hasNull) {
                    if (hasNull && cropConfig?.irrigation !== null) {
                        handleChange('irrigation')(null);
                    }
                } else {
                    handleChange('irrigation')(irrigationChoices.length > 0 ? irrigationChoices[0].value : null);
                }
            }
        }
    }, [crop, cropConfig?.irrigation, soilTypesQuery.data]);

    const enableEnvironmentQuery =
        crop === 'soy' ||
        (![undefined, null].includes(cropConfig?.soilMoisture) &&
            (!soilTypesQuery.isValidating || !enableSoilTypesQuery) &&
            (!soilTypesQuery.data ||
                soilTypesQuery.data?.irrigationChoices
                    ?.map((el) => (typeof el?.value === 'number' ? el.value : null))
                    ?.includes(cropConfig?.irrigation)) && // Si la opcion seleccionada no esta en las listadas
            (!soilTypesQuery.data ||
                soilTypesQuery.data?.soilTypesChoices
                    ?.map((el) => (typeof el?.value === 'number' ? el.value : null))
                    ?.includes(cropConfig?.soilType))); // Si la opcion seleccionada no esta en las listadas

    const environmentsQuery = useEnvironments(
        {
            lat: field?.lat,
            lng: field?.lng,
            countryCode: field?.country?.code,
            climatePattern: cropConfig?.climatePattern,
            soilMoisture: cropConfig?.soilMoisture,
            crop: cropConfig?.crop,
            plantingDate: cropConfig?.plantingDate,
            soilType: cropConfig?.soilType,
            irrigation: cropConfig?.irrigation,
        },
        {
            enabled: enableEnvironmentQuery,
        }
    );

    useEffect(() => {
        const environments = environmentsQuery.data;
        if (!environments) return;

        handleChange('recommendedEnvironment')(environments);
        handleChange('environment')(environments.default);
    }, [environmentsQuery.data]);

    const cropBrandCodenames = (sessionBrands?.[crop] || [])
        .map((brand) => brand.codename)
        .filter(Boolean)
        .join(',');

    const productsQuery = useProducts(
        {
            lat: field?.lat,
            lng: field?.lng,
            crop: cropConfig?.crop,
            plantingDate: cropConfig?.plantingDate,
            brandCodename: cropBrandCodenames,
            environment: cropConfig?.environment,
        },
        { enabled: !!cropBrandCodenames && !!cropConfig?.environment }
    );

    useEffect(() => {
        const products = [
            ...(productsQuery.data?.featuredProducts || []),
            ...(productsQuery.data?.otherProducts || []),
        ];
        if (productProfile && products.some((product) => product.id === Number(productProfile))) {
            handleChange('products')([Number(productProfile)]);
        }
    }, [productProfile, productsQuery.data?.featuredProducts, productsQuery.data?.otherProducts]);

    const isLoading =
        productsQuery.isLoading ||
        environmentsQuery.isLoading ||
        soilTypesQuery.isLoading ||
        soilMoisturesQuery.isLoading;

    // Helpers

    const mappedProducts = useMemo(() => {
        return [...(productsQuery.data?.featuredProducts || []), ...(productsQuery.data?.otherProducts || [])].reduce(
            (pV, cV) => {
                pV[cV.id] = cV;
                return pV;
            },
            {}
        );
    }, [productsQuery.data?.featuredProducts, productsQuery.data?.otherProducts]);
    const featuredProducts = useMemo(
        () => productsQuery.data?.featuredProducts || [],
        [productsQuery.data?.featuredProducts]
    );
    const otherProducts = useMemo(() => productsQuery.data?.otherProducts || [], [productsQuery.data?.otherProducts]);

    const handleProductToggle = (product) => {
        return () => {
            let products = cropConfig['products'] || [];
            if (cropConfig['products'].includes(product.id)) {
                products = products.filter((id) => id !== product.id);
            } else if (cropConfig['products'].length < settings.maxProductSelection) {
                products = [...products, product.id];
            }
            handleChange('products')(products);
        };
    };

    const metricChoices = useMemo(() => {
        const availableMetrics = [];
        [featuredProducts, otherProducts].forEach((products) => {
            products.forEach(({ metrics }) => {
                metrics.forEach((metric) => {
                    if (!availableMetrics.some((m) => m.id === metric.id)) {
                        availableMetrics.push(metric);
                    }
                });
            });
        });
        return availableMetrics;
    }, [featuredProducts, otherProducts]);

    const validPlantingDates = getValidPlantingDates(cropConfig, field);
    const climatePatternChoices = getClimatePatternChoices();
    const pestChoices = usePestChoices(cropConfig, field);

    return {
        soilMoisturesQuery,
        soilTypesQuery,
        environmentsQuery,
        productsQuery,
        isLoading,
        handleProductToggle,
        validPlantingDates,
        climatePatternChoices,
        pestChoices,
        metricChoices,
        featuredProducts,
        otherProducts,
        mappedProducts,
    };
};

export const useRecommendationResultData = ({ field, crop, cropConfig, mappedProducts }) => {
    const metricsQuery = useMetricsForProducts({ crop, productsId: cropConfig.products });
    const featuresQuery = useFeaturesForProducts({ crop, productsId: cropConfig.products });
    const soyPlantingRecommendationQuery = useAllSoyPlantingRecommendation({
        environment: cropConfig.environment,
        plantingDate: cropConfig.plantingDate,
        productsId: cropConfig.products,
        crop,
    });
    const cornDensityCurveQuery = useCornDensityCurveForProducts({
        productsId: cropConfig.products,
        environment: cropConfig.environment,
        minValue: 3,
        maxValue: 12,
        step: 0.05,
        crop,
    });
    const fertilizationCoefficientsQuery = useFertilizationCoefficientsForProducts({
        environment: cropConfig.environment,
        products: cropConfig.products.map((pId) => ({
            estimatedYield: cornDensityCurveQuery.data?.[pId]?.recommended?.yield,
            id: pId,
        })),
    });

    const yieldByEnvironmentQuery = useYieldByEnvironmentForProducts({
        environment: cropConfig.environment,
        productsId: cropConfig.products,
        harvestDensity: cropConfig.userDensity / 10000,
    });
    const phenologyQuery = usePhenologyForProducts({
        productsId: cropConfig.products,
        lat: field?.lat,
        lng: field?.lng,
        plantingDate: cropConfig?.plantingDate,
        crop,
    });

    const pestControlPlans = usePestControlPlans({ crop: cropConfig.crop, country: field?.country.code });
    const catalogBrands = useSelector(getCatalogBrands);

    const controlApplicationsQuery = useGetCornDiseaseControlApplications({ page: 1, pageSize: 10000 });

    const productsWithAPIData = useMemo(
        () =>
            cropConfig.products.map((pId) => {
                const cornDensityCurveQueryRecommended = cornDensityCurveQuery.data?.[pId]?.recommended;
                const differential =
                    cornDensityCurveQueryRecommended?.yield - yieldByEnvironmentQuery.data?.[pId]?.yield;

                const diseases = (controlApplicationsQuery.data?.results || []).filter(
                    (controlApplication) => controlApplication.cornProductId === pId
                );

                const phenologies = phenologyQuery.data?.[pId];

                const output = {
                    ...mappedProducts?.[pId],

                    metrics: metricsQuery.data?.[pId] || [],
                    features: featuresQuery.data?.[pId] || [],
                    fertilization: {
                        ...fertilizationCoefficientsQuery.data?.[pId],
                        n: {
                            ...fertilizationCoefficientsQuery.data?.[pId]?.n,
                        },
                    },

                    // Deprecated
                    ...(crop === 'corn' && {
                        harvestDensity: {
                            recommended:
                                cornDensityCurveQueryRecommended?.density &&
                                cornDensityCurveQueryRecommended?.density * 10000,
                            user: cropConfig.userDensity,
                        },
                        estimatedYield: {
                            forRecommendedHarvestDensity: cornDensityCurveQueryRecommended?.yield,
                            forUserHarvestDensity: yieldByEnvironmentQuery.data?.[pId]?.yield,
                            differentials: {
                                ...(isNaN(differential)
                                    ? {}
                                    : {
                                          forUserHarvestDensity: {
                                              value: differential,
                                              percentage:
                                                  100 * (differential / cornDensityCurveQueryRecommended?.yield),
                                          },
                                      }),
                            },
                        },
                        estimatedYieldByHarvestDensity: cornDensityCurveQuery.data?.[pId]?.points.map((p) => ({
                            estimatedYield: p.yield,
                            density: p.density * 10000,
                        })),
                        phenology: [
                            phenologies?.planting,
                            phenologies?.emergence,
                            phenologies?.flowering,
                            phenologies?.maturity,
                        ],
                    }),
                };

                // Deprecated
                output.profile = {
                    label: output.catalogName,

                    densityTableValues: mappedProducts?.[pId]?.densityTableValues || [],
                    metrics: output.metrics || [],
                    features: output.features || [],
                    nutritionalInfo: output.nutritionalInfo || [],
                    diseases: diseases || [],
                    refugeOptions: output?.refugeOptions?.split(' / ') || [],
                    technologies: output.technologies || [],

                    ...(crop === 'soy' && {
                        cycle: soyPlantingRecommendationQuery.data?.[pId]?.cycles?.[0]?.value,
                        offSeasonStart: soyPlantingRecommendationQuery.data?.[pId]?.waterAvailableDates?.offSeasonStart,
                        excellentStart: soyPlantingRecommendationQuery.data?.[pId]?.waterAvailableDates?.excellentStart,
                        reducedStart: soyPlantingRecommendationQuery.data?.[pId]?.waterAvailableDates?.reducedStart,
                    }),

                    product: {
                        seedsPerBag: output.seedsPerBag,
                        brand: output.brand,
                        catalogName: output.catalogName,
                        name: output.name,
                    },
                };

                return output;
            }),
        [
            cropConfig.products,
            cropConfig.userDensity,
            cornDensityCurveQuery.data,
            yieldByEnvironmentQuery.data,
            controlApplicationsQuery.data,
            phenologyQuery.data,
            mappedProducts,
            metricsQuery.data,
            featuresQuery.data,
            fertilizationCoefficientsQuery.data,
            crop,
            soyPlantingRecommendationQuery.data,
        ]
    );

    return {
        metricsQuery,
        featuresQuery,
        soyPlantingRecommendationQuery,
        cornDensityCurveQuery,
        fertilizationCoefficientsQuery,
        pestControlPlans,
        catalogBrands,
        productsWithAPIData,
    };
};

export const usePestChoices = (cropConfig, field) => {
    const { data: filteredPestControlPlans } = usePestControlPlans({
        crop: cropConfig.crop,
        country: field?.country.code,
    });
    const { data: allPests } = usePests({});

    if (!cropConfig || !field) return [];

    const getPestControlPlansByPest = (plans) =>
        plans?.reduce((hash, plan) => {
            if (!hash[plan.pest.id]) hash[plan.pest.id] = [];
            hash[plan.pest.id].push(plan);
            return hash;
        }, {});

    const getControlPlanConfigValue = (controlPlan, key, language = 'en') => {
        if (!controlPlan[key]) return undefined;
        return controlPlan[key];
    };

    const getConfigChoicesByValue = (controlPlans, key) => {
        return controlPlans?.reduce((hash, controlPlan) => {
            const value = getControlPlanConfigValue(controlPlan, key);
            if (!value) return hash;

            if (!hash[value]) hash[value] = [];
            hash[value] = controlPlan[key];
            return hash;
        }, {});
    };

    const controlPlansByPest = getPestControlPlansByPest(filteredPestControlPlans?.results || []);

    const configKeys = ['cropStage', 'damageLevel', 'pestSize', 'seedTreatment'];
    const configChoices = configKeys.reduce(
        (hash, key) => ({ ...hash, [key]: getConfigChoicesByValue(filteredPestControlPlans?.results || [], key) }),
        {}
    );
    const pests = allPests?.results?.filter(({ id }) => controlPlansByPest[id] && !!controlPlansByPest?.[id]?.length);

    const groupByConfigValue = (controlPlans, configKey) => {
        if (!controlPlans?.some((controlPlan) => !!controlPlan[configKey])) return controlPlans;
        return groupBy(controlPlans, (controlPlan) => {
            if (!controlPlan[configKey]) return '';
            return getControlPlanConfigValue(controlPlan, configKey);
        });
    };

    const getDefaultControlPlan = (configs) => {
        return [configs[0]].reduce((_, config) => {
            if (config.controlPlan) return config.controlPlan;
            return getDefaultControlPlan(config.configuration);
        }, {});
    };

    const getResultKey = (result) => (Array.isArray(result) ? 'configuration' : 'controlPlan');

    const buildConfigurations = (controlPlans, configKey) => {
        if (!configKey) return controlPlans[0];

        const nextKey = configKeys[configKeys.indexOf(configKey) + 1];

        controlPlans = controlPlans.sort((a, b) => a.order - b.order);
        const controlPlansByConfig = groupByConfigValue(controlPlans, configKey);

        if (Array.isArray(controlPlansByConfig)) return buildConfigurations(controlPlansByConfig, nextKey);

        return Object.entries(controlPlansByConfig).reduce((hash, [configValue, configControlPlans]) => {
            const result = buildConfigurations(configControlPlans, nextKey);
            const label = configChoices[configKey][configValue];
            hash.push({
                key: configKey,
                value: configValue,
                label: label || { en: 'None', es: 'Ninguno', pt: 'Nenhum' },
                [getResultKey(result)]: result,
            });
            return hash;
        }, []);
    };

    return pests
        ?.map((pest) => {
            const configurations = buildConfigurations(controlPlansByPest[pest.id], configKeys[0]);
            return {
                pest,
                configurations,
                defaultControlPlan: getDefaultControlPlan(configurations),
            };
        })
        .sort((a, b) => a.order - b.order || localeSort('name')(a, b));
};
